import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { inr } from "@/lib/format";
import { TrendingUp, TrendingDown, Scale, ReceiptIndianRupee, Wallet, HandCoins } from "lucide-react";

export default function Financials() {
  const [mode, setMode] = useState("fy");   // fy | custom
  const [fy, setFy] = useState("");
  const [fys, setFys] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/financials/options").then(({ data }) => {
      setFys(data.fys);
      setFy(data.current_fy);
    });
  }, []);

  useEffect(() => {
    if (mode === "fy" && !fy) return;
    if (mode === "custom" && (!range.from || !range.to)) return;
    const params = mode === "fy" ? { fy } : { from: range.from, to: range.to };
    api.get("/financials", { params }).then(({ data }) => setData(data));
  }, [mode, fy, range.from, range.to]);

  const s = data;

  return (
    <div>
      <PageHeader title="Financial Summary" subtitle="Balance sheet & P&L snapshot for a selected period" />
      <div className="px-8 py-6 space-y-6">
        {/* Period selector */}
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-4 flex-wrap">
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList>
                <TabsTrigger value="fy" data-testid="mode-fy">Financial Year</TabsTrigger>
                <TabsTrigger value="custom" data-testid="mode-custom">Custom Range</TabsTrigger>
              </TabsList>
            </Tabs>
            {mode === "fy" ? (
              <div className="min-w-[200px]">
                <Select value={fy} onValueChange={setFy}>
                  <SelectTrigger data-testid="fy-select"><SelectValue placeholder="Choose FY" /></SelectTrigger>
                  <SelectContent>
                    {fys.map((f) => (<SelectItem key={f} value={f}>FY {f}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div>
                  <Label className="text-xs text-slate-600">From</Label>
                  <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">To</Label>
                  <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
                </div>
              </div>
            )}
            {s?.period && (
              <div className="text-xs text-slate-500 ml-auto">
                {s.period.from} <span className="mx-1">→</span> {s.period.to}
              </div>
            )}
          </div>
        </Card>

        {!s ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <>
            {/* Top KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Total Sales" value={inr(s.sales.total)} icon={TrendingUp} color="text-emerald-600 bg-emerald-50" />
              <Kpi label="Total Purchases" value={inr(s.purchases.po_total + s.purchases.bills_total)} icon={TrendingDown} color="text-sky-600 bg-sky-50" />
              <Kpi label="Gross Profit" value={inr(s.gross_profit)} icon={Scale} color={s.gross_profit >= 0 ? "text-emerald-600 bg-emerald-50" : "text-rose-600 bg-rose-50"} sub="Sales − PO subtotal (ex GST)" />
              <Kpi label="Net Cash Flow" value={inr(s.cashflow.net)} icon={Wallet} color={s.cashflow.net >= 0 ? "text-indigo-600 bg-indigo-50" : "text-amber-600 bg-amber-50"} sub="Received − Paid" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Receivables (open)" value={inr(s.outstanding.receivable)} icon={HandCoins} color="text-indigo-600 bg-indigo-50" />
              <Kpi label="Payables (open)" value={inr(s.outstanding.payable)} icon={Wallet} color="text-amber-600 bg-amber-50" />
              <Kpi label="GST Collected" value={inr(s.gst.output)} icon={ReceiptIndianRupee} color="text-emerald-600 bg-emerald-50" />
              <Kpi label="GST Net Liability" value={inr(s.gst.net_liability)} icon={ReceiptIndianRupee} color={s.gst.net_liability >= 0 ? "text-rose-600 bg-rose-50" : "text-emerald-600 bg-emerald-50"} sub="Output − Input" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Monthly chart */}
              <Card className="p-6 border-slate-200 lg:col-span-2">
                <h3 className="font-display text-lg font-bold mb-2">Sales & Purchases by Month</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={s.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => inr(v)} />
                      <Legend />
                      <Bar dataKey="sales" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="purchases" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* P&L summary */}
              <Card className="p-6 border-slate-200 space-y-2 text-sm">
                <h3 className="font-display text-lg font-bold mb-1">P&L Summary</h3>
                <Row label="Sales (ex GST)" v={s.sales.subtotal} />
                <Row label="Sales GST (Output)" v={s.gst.output} muted />
                <Row label="Sales (incl GST)" v={s.sales.total} bold />
                <div className="border-t border-slate-200 mt-2" />
                <Row label="PO Purchases (ex GST)" v={s.purchases.po_subtotal} />
                <Row label="Supplier Bills (ex GST)" v={s.purchases.bills_subtotal} />
                <Row label="Input GST (POs + Bills)" v={s.gst.input_po + s.gst.input_bills} muted />
                <div className="border-t border-slate-200 mt-2" />
                <Row label="Gross Profit (ex GST)" v={s.gross_profit} bold accent={s.gross_profit >= 0 ? "text-emerald-700" : "text-rose-700"} />
                <Row label="Net GST Payable" v={s.gst.net_liability} accent={s.gst.net_liability >= 0 ? "text-rose-700" : "text-emerald-700"} />
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Top Customers</div>
                <Table className="data-table">
                  <TableHeader>
                    <TableRow><TableHead>Name</TableHead><TableHead className="text-right">Sales</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {s.top_customers.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-6 text-slate-500">No data</TableCell></TableRow>}
                    {s.top_customers.map((c) => (
                      <TableRow key={c.name}><TableCell>{c.name}</TableCell><TableCell className="text-right number-cell font-semibold">{inr(c.amount)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              <Card className="border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Top Suppliers</div>
                <Table className="data-table">
                  <TableHeader>
                    <TableRow><TableHead>Name</TableHead><TableHead className="text-right">Purchases</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {s.top_suppliers.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-6 text-slate-500">No data</TableCell></TableRow>}
                    {s.top_suppliers.map((c) => (
                      <TableRow key={c.name}><TableCell>{c.name}</TableCell><TableCell className="text-right number-cell font-semibold">{inr(c.amount)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>

            <Card className="p-6 border-slate-200 text-xs text-slate-500">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Invoices" v={s.counts.invoices} />
                <Stat label="Purchase Orders" v={s.counts.purchase_orders} />
                <Stat label="Supplier Bills" v={s.counts.supplier_bills} />
                <Stat label="Payments" v={s.counts.payments} />
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, sub }) {
  return (
    <Card className="p-5 border-slate-200 card-hover">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-4 text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold number-cell">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

function Row({ label, v, bold, muted, accent }) {
  return (
    <div className={`flex justify-between ${muted ? "text-slate-500" : ""} ${bold ? "font-semibold" : ""} ${accent || ""}`}>
      <span>{label}</span><span className="number-cell">{inr(v)}</span>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display text-lg font-bold text-slate-700">{v}</div>
    </div>
  );
}
